import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Accept, Cancel } from 'Common/Components/Button/styled';
import StandardDropdown from 'Common/Components/StandarDropdown';
import StyledCheckbox from 'Common/Components/StyledCheckbox';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import trim from 'lodash/trim';
import ToolParams from 'Screens/Automation/Agents/components/AgentDetail/components/ToolParams';
import { saveSchedule } from 'store/Schedule/actions';
import { selectUserAgents, selectSchedulerSelected, selectSelectedSection } from 'store/Schedule/selectors';
import CronExpression from './components/CronExpression';
import AgentsDropdown from './components/AgentsDropdown';
import WorkspacesSelector from './components/WorkspacesSelector';
import {
  Wrapper, WrapperTitle, Title, ButtonsWrapper, DescriptionSection,
  Label, Description, Subtitle, WrapperToolParams, WrapperAgent, AgentSection,
  Content
} from './styled';
import ModalWrapper from 'Common/Components/ReduxModal/styled';
import { closeModal } from 'store/modals/actions';
import { MODAL_SCHEDULE_CREATE_EDIT } from 'store/modals/modals';
import CreationWarning from 'Common/Components/CreationWarning';
import useModalShortcuts from 'Hooks/useModalShortcuts';

const CreateEditSchedule = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const schedule = useSelector(selectSchedulerSelected);
  const section = useSelector(selectSelectedSection);
  const userAgents = useSelector(selectUserAgents);
  const [description, setDescription] = useState('');
  const [active, setActive] = useState(true);
  const [currentAgent, setCurrentAgent] = useState();
  const [currentTool, setCurrentTool] = useState({});
  const [currentToolParams, setCurrentToolParams] = useState({});
  const [crontab, setCrontab] = useState({ name: 'day', expression: '0 4 * * *' });
  const [severityRange, setSeverityRange] = useState({
    min: { value: 0, severity: 'unclassified' },
    max: { value: 5, severity: 'critical' }
  });
  const tools = get(currentAgent, 'value.executors', []);
  const mappedTools = tools.map((t) => ({ name: t.name, desc: t.name }));
  const [scheduleWorkspaces, setScheduleWorkspaces] = useState([]);
  const parsedAgents = () => {
    if ((section === 'scheduledAgents') && !isEmpty(userAgents)) return userAgents.map((t) => ({ label: get(t, 'name', ''), value: t }));
    else return [];
  };
  const [showErrorModal, setShowErrorModal] = useState(false);
  const createWarningMessage = intl.formatMessage({ id: 'modal.createVuln.cantCreate.message' });

  const onChangeAgent = (obj) => {
    setCurrentAgent(obj);
    setCurrentTool({});
    setCurrentToolParams({});
  };

  const onChangeTool = (field, value) => {
    const tool = get(currentAgent, 'value.executors', get(schedule, 'value.agent.executors')).find((t) => t.name === value);
    setCurrentTool(tool);
  };

  const onChangeField = (field, value) => {
    const newParams = { ...currentToolParams };
    newParams[field] = value;
    if (!trim(value)) delete newParams[field];
    setCurrentToolParams(newParams);
  };

  const disableAccept = () => {
    const currentToolIsInvalid = () => isEmpty(currentTool);
    const currentToolParamsIsInvalid = () => {
      if (!isEmpty(currentTool.parameters_metadata) && isEmpty(currentToolParams)) return true;
      if (isEmpty(currentTool)) return true;
      if (isEmpty(currentTool.parameters_metadata)) return false;

      const params = currentTool.parameters_metadata;
      return Object.keys(params).some((p) => params[p].mandatory && isEmpty(currentToolParams[p]));
    };

    if ((isEmpty(currentAgent) || !description || currentToolIsInvalid() || currentToolParamsIsInvalid() || !crontab || isEmpty(scheduleWorkspaces))) return true;

    return false;
  };

  const onClose = () => dispatch(closeModal(MODAL_SCHEDULE_CREATE_EDIT));

  const onAccept = () => {
    if (!disableAccept()) {
      const workspacesNames = scheduleWorkspaces.map((ws) => {
        if (ws.name) {
          return ws.name;
        } else {
          return ws;
        }
      });
      dispatch(saveSchedule(0, currentAgent.value.id, crontab.name, active, crontab.expression, description, currentTool.id, JSON.stringify(currentToolParams), workspacesNames, Intl.DateTimeFormat().resolvedOptions().timeZone, severityRange));
      onClose();
    } else setShowErrorModal(true);
  };

  useModalShortcuts({
    id: 'schedule-create-edit',
    onSave: onAccept,
    onCancel: onClose,
    canSave: !disableAccept(),
    saveLabel: intl.formatMessage({ id: 'schedule.createEditSchedule.accept' })
  });

  const title = intl.formatMessage({ id: 'schedule.createEditSchedule.create.title' });

  const onChangeActive = () => {
    setActive(!active);
  };

  return (
    <ModalWrapper>
      <Wrapper>
        <WrapperTitle>
          <Title>{ title }</Title>
          <ButtonsWrapper>
            <Cancel id="scheduler-modal-cancel" onClick={ onClose }>{ intl.formatMessage({ id: 'schedule.createEditSchedule.cancel' }) }</Cancel>
            <Accept id="scheduler-modal-accept" onClick={ onAccept }>{ intl.formatMessage({ id: 'schedule.createEditSchedule.accept' }) }</Accept>
          </ButtonsWrapper>
        </WrapperTitle>

        <Content>
          <DescriptionSection>
            <Label>{ intl.formatMessage({ id: 'schedule.createEditSchedule.description' }) }</Label>
            <Description id="scheduler-modal-description" onChange={ (e) => setDescription(e.target.value) } value={ description } placeholder={ intl.formatMessage({ id: 'schedule.createEditSchedule.description.placeholder' }) } />
            <StyledCheckbox id="scheduler-modal-active" checked={ active } label={ intl.formatMessage({ id: 'schedule.createEditSchedule.active' }) } onChange={ onChangeActive } />
          </DescriptionSection>

          <CronExpression crontab={ crontab } onChangeCrontab={ (value) => setCrontab(value) } isCreateSchedule />

          <AgentSection>
            <Subtitle>{ intl.formatMessage({ id: 'schedule.createEditSchedule.agentSelection' }) }</Subtitle>
            <WrapperAgent>
            <AgentsDropdown id="scheduler-modal-agent" options={ parsedAgents() } updateValue={ onChangeAgent } value={ currentAgent } placeholder={ intl.formatMessage({ id: 'schedule.createEditSchedule.dropdownAgent.placeholder' }) } />
            </WrapperAgent>

            { (!isEmpty(currentAgent) || !isEmpty(get(schedule, 'value.agent', null))) &&
              (<>
                  <Subtitle>{ intl.formatMessage({ id: 'schedule.createEditSchedule.toolSelection' }) }</Subtitle>
                  <StandardDropdown id="scheduler-modal-tool" defaultValue={ get(currentTool, 'name', '') } width="200px" field="toolParams" options={ mappedTools } updateValue={ onChangeTool } placeholder={ intl.formatMessage({ id: 'schedule.createEditSchedule.dropdownTool.placeholder' }) } />
                  { currentTool?.name
                    ? <WrapperToolParams>
                        <ToolParams
                          currentTool={ currentTool }
                          defaultParams={ currentToolParams }
                          onChangeField={ onChangeField }
                          severityRange={ severityRange }
                          setSeverityRange={ setSeverityRange }
                        />
                      </WrapperToolParams>
                    : null
                  }
               </>
              ) }
          </AgentSection>
          <WorkspacesSelector scheduleWorkspaces={ scheduleWorkspaces } setScheduleWorkspaces={ setScheduleWorkspaces } />
        </Content>
      </Wrapper>
      { showErrorModal && <CreationWarning errorTitle={ title } errorMessage={ createWarningMessage } onClose={ () => setShowErrorModal(false) } /> }
    </ModalWrapper>
  );
};

export default CreateEditSchedule;
